import { useState } from "react";
import { useCartContext } from "./useCart";
import { MainTracker } from "../PixelTrackers/MainTracker";
import { translate } from "../utils/translator";
import * as http from "../utils/http";

export type CouponWidgetState = {
  inputCode: string;
  errorMsg?: string;
  exposeExtraEnterContainer: boolean;
  additionalCouponData?: {
    minimum_price_required: number;
    treatment_fee_fixed: number;
    service_fee_fixed: number;
  };
  isSubmitting?: boolean;
};

type UseCouponWidgetOptions = {
  language?: string;
  isExpressSelected?: boolean;
  isOrderSubmitting?: () => boolean;
  setSubmitButtonDisabled?: (disabled: boolean) => void;
  onCartView?: (cartView: any) => void;
};

export const useCouponWidget = ({
  language,
  isExpressSelected = false,
  isOrderSubmitting = () => false,
  setSubmitButtonDisabled,
  onCartView,
}: UseCouponWidgetOptions) => {
  const { getCart, saveCart } = useCartContext();
  const [couponWidgetState, setCouponWidgetState] = useState<CouponWidgetState>({
    inputCode: "",
    errorMsg: "",
    exposeExtraEnterContainer: false,
  });

  const handleApplyCoupon = async ({
    inputCode,
    shouldOverrideCartBonuses = true,
    isExpressSelected: selectedExpress = isExpressSelected,
  }: {
    inputCode: string;
    shouldOverrideCartBonuses?: boolean;
    isExpressSelected?: boolean;
  }) => {
    if (isOrderSubmitting()) return;

    setSubmitButtonDisabled?.(true);
    setCouponWidgetState((current) => ({ ...current, isSubmitting: true }));

    try {
      const response = await http.postToServer(
        "cart/activate-coupon",
        { inputCode, shouldOverrideCartBonuses, isExpressSelected: selectedExpress },
        [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID],
      );
      const cartView = response.data.cartView;

      if (cartView) {
        saveCart({ ...cartView.cart, products: cartView.products, timekitBooking: cartView.timekitBooking });
        onCartView?.(cartView);
      }

      const cart = getCart();
      setCouponWidgetState((current) => ({ ...current, inputCode, exposeExtraEnterContainer: false }));
      MainTracker.superProperties({
        "Coupon Amount": cart.bonus_value,
        "Coupon Amount EUR": cart.bonus_value,
        "Coupon ID": cart.bonus_id,
        "Coupon Code": inputCode,
      });
      MainTracker.track("click", "Apply Coupon", { coupon_code: inputCode });
    } catch (error: any) {
      const responseMsg = error.response?.data?.message || "Network Error";
      const cartView = error.response?.data?.cartView;
      window.clientLogger.warn("Failed to activate coupon in DB (cart)", error);
      sessionStorage.removeItem("cpn");

      if (cartView) {
        saveCart({ ...cartView.cart, products: cartView.products });
      }

      MainTracker.track("click", "Apply Coupon Failed", { "Coupon Code": inputCode, "Coupon Error": responseMsg });
      setCouponWidgetState((current) => ({
        ...current,
        inputCode,
        errorMsg: translate(language, "shipping", `CouponWidget/${responseMsg}`),
        additionalCouponData: {
          minimum_price_required: error.response?.data?.minimum_price_required || 0,
          treatment_fee_fixed: error.response?.data?.treatment_fee_fixed || 0,
          service_fee_fixed: error.response?.data?.service_fee_fixed || 0,
        },
      }));
    } finally {
      setCouponWidgetState((current) => ({ ...current, isSubmitting: false }));
      setSubmitButtonDisabled?.(false);
    }
  };

  return { couponWidgetState, setCouponWidgetState, handleApplyCoupon };
};
